import {
  BarChart3,
  Bell,
  CalendarDays,
  ClipboardList,
  GitBranch,
  LayoutDashboard,
  Package,
  Palette,
  PanelLeft,
  PanelLeftClose,
  Shield,
  Truck,
  Tv,
  Users,
} from 'lucide-react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { ROUTES } from '@/config';
import { useAuth } from '@/hooks';
import { cn } from '@/lib';
import { useUiStore } from '@/stores';

const navItems = [
  { to: ROUTES.ADMIN_TASKS, icon: LayoutDashboard, labelKey: 'nav.tasks' },
  { to: ROUTES.ADMIN_SHOWS, icon: Tv, labelKey: 'nav.shows' },
  { to: ROUTES.ADMIN_WORKFLOWS, icon: GitBranch, labelKey: 'nav.workflows' },
  { to: ROUTES.ADMIN_PLANNER, icon: CalendarDays, labelKey: 'nav.planner' },
  { to: ROUTES.ADMIN_EQUIPMENT, icon: Package, labelKey: 'nav.equipment' },
  { to: ROUTES.ADMIN_VEHICLES, icon: Truck, labelKey: 'nav.vehicles' },
  { to: ROUTES.ADMIN_GRAPHICS, icon: Palette, labelKey: 'nav.graphics' },
  { to: ROUTES.ADMIN_TEAM, icon: Users, labelKey: 'nav.team' },
  { to: ROUTES.ADMIN_ROLES, icon: Shield, labelKey: 'nav.roles' },
  { to: ROUTES.ADMIN_REPORTS, icon: BarChart3, labelKey: 'nav.reports' },
  {
    to: ROUTES.ADMIN_NOTIFICATION_SETTINGS,
    icon: Bell,
    labelKey: 'nav.notificationSettings',
    // Backend allows notification rules and templates to system/tenant admins only.
    adminOnly: true,
  },
];

export function AdminSidebar() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useUiStore();

  const handleNavClick = useCallback(() => {
    // Auto-close sidebar on mobile when navigating
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  }, [setSidebarOpen]);

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-50 flex h-screen flex-col border-r border-admin-border bg-admin-sidebar transition-all duration-200',
        // Mobile: slide in/out as overlay
        'lg:z-30',
        sidebarOpen ? 'w-60 translate-x-0' : '-translate-x-full lg:translate-x-0 lg:w-16'
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-admin-border px-4">
        {sidebarOpen && <img src="/momcilo_logo.svg" alt="Momcilo" className="h-6" />}
        <button
          type="button"
          onClick={toggleSidebar}
          className="rounded-lg p-2 text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text"
          aria-label="Toggle sidebar"
        >
          {sidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeft className="h-5 w-5" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-2 py-4">
        {navItems
          .filter(({ adminOnly }) => !adminOnly || isAdmin)
          .map(({ to, icon: Icon, labelKey }) => (
            <NavLink
              key={to}
              to={to}
              onClick={handleNavClick}
              // Collapsed, the link is icon-only: without these it has no
              // accessible name and no tooltip at all.
              aria-label={t(labelKey)}
              title={t(labelKey)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text'
                )
              }
            >
              <Icon className="h-5 w-5 shrink-0" />
              {sidebarOpen && <span>{t(labelKey)}</span>}
            </NavLink>
          ))}
      </nav>

      {/* Worker view link for non-admin users */}
      {!isAdmin && (
        <div className="border-t border-admin-border px-2 py-3">
          <NavLink
            to={ROUTES.WORKER}
            onClick={handleNavClick}
            aria-label={t('nav.myTasks')}
            title={t('nav.myTasks')}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text transition-colors"
          >
            <ClipboardList className="h-5 w-5 shrink-0" />
            {sidebarOpen && <span>{t('nav.myTasks')}</span>}
          </NavLink>
        </div>
      )}
    </aside>
  );
}
